import { useState, useMemo, useCallback } from 'react'
import { type DashTableFetchData } from 'legacy/dash-blocks/dash-table/utils/dashTable.types'
import {
  dataFetchingLoading,
  type DataFetchingState,
  dataFetchingSuccess,
  dataFetchingError,
} from 'legacy/common/utils/data-fetching/dataFetching.types'
import { useRetailerId, usePartnerId } from 'utils/routing/params.hooks'
import { ordersListApiFetchOrders } from 'legacy/sections/orders/components/list/utils/ordersList.api'
import {
  type OrdersListApiResponse,
  type OrdersListApiOrder,
} from 'legacy/sections/orders/components/list/utils/ordersList.types'
import { useOrdersListQueryParamsBehaviour } from 'legacy/sections/orders/components/list/utils/ordersListQueryParams.hooks'
import { type OrderType } from 'legacy/sections/orders/utils/orders.types'

export interface UseOrdersListBehaviourProps {
  orderType: OrderType
}

const initialApiState = dataFetchingLoading<OrdersListApiResponse>()

export const useOrdersListBehaviour = ({ orderType }: UseOrdersListBehaviourProps) => {
  const partnerId = usePartnerId()
  const retailerId = useRetailerId()

  const queryParamsBehaviour = useOrdersListQueryParamsBehaviour()

  const [ordersApiState, setOrdersApiState] =
    useState<DataFetchingState<OrdersListApiResponse>>(initialApiState)

  const fetchOrdersFilters = {
    partnerId,
    retailerId,
    orderType,
    orderNumber: queryParamsBehaviour.orderNumber,
    orderStatus: queryParamsBehaviour.orderStatus,
    orderSource: queryParamsBehaviour.orderSource,
    dateSelectRangeValue: queryParamsBehaviour.dateSelectRangeValue,
    retailerLocationSelectValue: queryParamsBehaviour.retailerLocationSelectValue,
  }

  // Trigger effects
  const fetchData: DashTableFetchData<typeof fetchOrdersFilters> = useCallback(
    ({ limit, offset, filters }) => {
      setOrdersApiState(previousState =>
        dataFetchingLoading({
          previousState,
        })
      )

      ordersListApiFetchOrders({ limit, offset, ...filters })
        .then(result => setOrdersApiState(dataFetchingSuccess(result)))
        .catch((err?: Error) => setOrdersApiState(dataFetchingError(err)))
    },
    []
  )

  const { approxGmv, orderCounts, filteredOrders, filteredOrderCount } = useMemo(() => {
    if (!ordersApiState.data) {
      return {
        filteredOrders: [] as OrdersListApiOrder[],
        approxGmv: undefined,
        orderCounts: undefined,
        filteredOrderCount: 0,
      }
    }

    const { orders, counts, approx_gmv: gmv } = ordersApiState.data.data
    const orderStatusKey = queryParamsBehaviour.orderStatus || 'all'

    // Defaults to undefined
    let calculatedCount

    if (counts) {
      calculatedCount = counts[orderStatusKey]
    }

    return {
      approxGmv: gmv,
      orderCounts: counts,
      filteredOrders: orders,
      filteredOrderCount: calculatedCount,
    }
  }, [ordersApiState.data, queryParamsBehaviour.orderStatus])

  return {
    fetchData,
    filters: fetchOrdersFilters,
    orderType,
    queryParamsBehaviour,
    approxGmv,
    orderCounts,
    filteredOrders,
    filteredOrderCount,
    error: ordersApiState.error,
    loading: ordersApiState.loading,
  }
}

export type OrdersListBehaviour = ReturnType<typeof useOrdersListBehaviour>
